import React, { CSSProperties, ReactElement } from 'react';
import { Box } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ReferenceLineProps,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

import { Paper, Typography } from '@occc/ui-design-system';
import { readableNumber } from '@occc-app/lib/format';
import {
  chartCartesianAxisColor,
  chartCartesianGridColor,
  chartDataColors,
  chartReferenceLineColor,
} from '@occc-app/themes/colors';
import {
  calcDateTime,
  DateTimeFormat,
  DateTimeFormatTZ,
  diffDateTime,
  DurationUnit,
  formatDateTime,
  formatDateTimeTZ,
  getUserTimezone,
  isAfterDateTime,
  isBeforeDateTime,
  maxDateTime,
  minDateTime,
  toLocalDateTime,
} from '@occc-app/lib/date';

import { DATA_KEYS } from '../constants';

const TICK_COUNT = 8;
const ESC_KEY_CODE = 27;

export type ChartSeries = {
  key: string;
  data: { x: Date; y: number }[];
  initialCumulativeValue: number;
};

type RechartsDataPoint = object & { x: string; date: Date };

type Props = {
  plotCumulative: boolean;
  series: ChartSeries[];
  rangeStart?: Date;
  rangeEnd?: Date;
  interval: DurationUnit;
  allowHighlighting?: boolean;
  formatTooltipTitle: (time: Date) => string;
  formatTooltipSeriesLabel: (key: string, time: Date) => string;
};

type LocalState = {
  highlightDateStart?: Date;
  highlightDateEnd?: Date;
};

// Note: This chart currently only supports a daily data point interval.
export class TimeSeriesChart extends React.Component<Props, LocalState> {
  state: LocalState = {};

  render() {
    const dateRange = this.calculateDateRange(this.props.series);
    const { keys: rechartsKeys, data: rechartsData } = this.translateToRecharts(
      this.props.series,
      dateRange,
    );

    // Recharts appears to support configuring the number of ticks to be shown on an axis
    // but we encountered challenges getting this setup for a time series chart. As a result
    // it seemed cleaner and easier to define the ticks to be shown explicitly.
    const ticks = this.calculateTicks(dateRange);

    // Some of these properties are not defined on the axis type definition (e.g. fontsize)
    // since they are passed directly through to underlying components. As a result they
    // cannot be defined directly as a prop of the component.
    const axisProps: any = {
      axisLine: false,
      fontSize: 12,
      stroke: chartCartesianAxisColor,
      tickLine: false,
    };
    // A solution to shrinking window not resizing the chart... because wat.
    // https://github.com/recharts/recharts/issues/1423#issuecomment-411098968
    const containerStyles: CSSProperties = {
      height: '100%',
      position: 'relative',
      width: '99%',
    };

    return (
      <div style={containerStyles}>
        <ResponsiveContainer>
          <ComposedChart
            data={rechartsData}
            margin={{ top: 20, right: 20, left: 15, bottom: 0 }}
            onMouseDown={this.startHighlight}
            onMouseMove={this.updateHighlight}
            onMouseUp={this.applyHighlight}
          >
            <XAxis {...axisProps} dataKey='x' ticks={ticks} tickMargin={10} />
            <YAxis
              {...axisProps}
              mirror={true}
              tickFormatter={this.formatYAxisTick}
              transform='translate(0 -8)'
              domain={['auto', 'auto']}
            />
            <CartesianGrid stroke={chartCartesianGridColor} vertical={false} />
            <Tooltip content={this.renderTooltip} />
            {this.constructSeries(rechartsKeys)}
            {this.renderHighlightedRange()}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  translateToRecharts(series: ChartSeries[], dateRange: { date: Date; label: string }[]) {
    const consolidatedData: RechartsDataPoint[] = [];
    const rechartsKeys: string[] = [];
    series.forEach((seriesDef) => {
      let cumulativeTotal = seriesDef.initialCumulativeValue;
      rechartsKeys.push(seriesDef.key);
      dateRange.forEach((date) => {
        let rechartsDataPoint: any = consolidatedData.find((d) => d.x === date.label);
        if (!rechartsDataPoint) {
          rechartsDataPoint = { x: date.label, date: date.date };
          consolidatedData.push(rechartsDataPoint);
        }
        const seriesDataPoint = seriesDef.data.find((d) => this.formatTime(d.x) === date.label);
        if (this.props.plotCumulative) {
          cumulativeTotal += seriesDataPoint ? seriesDataPoint.y : 0;
          rechartsDataPoint[seriesDef.key] = cumulativeTotal;
        } else {
          rechartsDataPoint[seriesDef.key] = seriesDataPoint ? seriesDataPoint.y : 0;
        }
      });
    });
    return { keys: rechartsKeys, data: consolidatedData };
  }

  calculateDateRange(series: ChartSeries[]) {
    let minDate = this.props.rangeStart;
    let maxDate = this.props.rangeEnd;

    if (!minDate || !maxDate || isBeforeDateTime(maxDate, minDate)) {
      const dates: Date[] = [];
      series.forEach((s) => s.data.forEach((d) => dates.push(d.x)));

      minDate = minDate || minDateTime(dates) || new Date();
      maxDate =
        (maxDate && isAfterDateTime(maxDate, minDate) ? maxDate : maxDateTime(dates)) || new Date();
    }

    const dateRange: { date: Date; label: string }[] = [];
    let date = minDate || new Date();
    while (isBeforeDateTime(date, maxDate)) {
      dateRange.push({ date: date, label: this.formatTime(date) });
      date = calcDateTime(date, 'add', 1, this.props.interval);
    }

    return dateRange;
  }

  constructSeries(seriesKeys: string[]) {
    const buildSeriesFor = (seriesId: any, index: any) =>
      index === 0
        ? this.constructPrimarySeries(seriesId)
        : this.constructSecondarySeries(seriesId, index - 1);
    return seriesKeys.map(buildSeriesFor);
  }

  constructPrimarySeries(seriesId: string) {
    return (
      <Area
        key={seriesId}
        type='linear'
        dataKey={seriesId}
        stroke='#288DD0'
        strokeWidth={2}
        fill='#288DD0'
        fillOpacity={0.15}
      />
    );
  }

  constructSecondarySeries(seriesId: string, index: number) {
    return (
      <Line
        key={seriesId}
        type='linear'
        dataKey={seriesId}
        stroke={chartDataColors[index % chartDataColors.length]}
        strokeDasharray='5 5'
        dot={false}
      />
    );
  }

  calculateTicks(dateRange: { date: Date; label: string }[]) {
    if (dateRange.length <= TICK_COUNT) {
      return dateRange.map((dataPoint) => dataPoint.label);
    }

    const ticks = [dateRange[0].label];
    const startDate = dateRange[0].date;
    const endDate = dateRange[dateRange.length - 1].date;

    const tickInterval = diffDateTime(endDate, startDate, this.props.interval) / TICK_COUNT;
    let date;
    for (let i = 1; i < TICK_COUNT; i++) {
      date = calcDateTime(dateRange[0].date, 'add', tickInterval * i, this.props.interval);
      ticks.push(this.formatTime(date));
    }
    ticks.push(dateRange[dateRange.length - 1].label);

    return ticks;
  }

  formatYAxisTick(tickValue: number) {
    return readableNumber(tickValue);
  }

  renderTooltip = (props: TooltipProps<ValueType, NameType>) => {
    const { active, label, payload } = props;
    if (!active) {
      return null;
    }

    const tooltipPayload: any = payload && payload.length && payload[0];
    const dataPointPayload: RechartsDataPoint = tooltipPayload && tooltipPayload.payload;
    const time = dataPointPayload && dataPointPayload.date;
    if (!time) {
      return null;
    }

    const filteredPayload = payload?.filter(
      (entry) =>
        (entry.dataKey === DATA_KEYS.REFERENCE_RANGE && entry.value !== 0) ||
        entry.dataKey !== DATA_KEYS.REFERENCE_RANGE,
    );

    const tooltipValues = filteredPayload!.map((tooltipEntry) => (
      <Typography key={tooltipEntry.name} variant='body2'>
        {this.props.formatTooltipSeriesLabel(tooltipEntry.name as string, time)}
        {' - '}
        {readableNumber((tooltipEntry.value as number) || 0)}
      </Typography>
    ));

    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant='subtitle2' component='p' color='primary' gutterBottom>
          {this.props.formatTooltipTitle(time) || label}
        </Typography>
        <Box px={1}>{tooltipValues}</Box>
      </Paper>
    );
  };

  formatTime(time: Date) {
    if (this.props.interval === 'hour') {
      const tz = formatDateTimeTZ(time, getUserTimezone(), DateTimeFormatTZ.ShortTimeZone);
      return formatDateTime(toLocalDateTime(time), DateTimeFormat.Time12hShort) + ' ' + tz;
    } else {
      return formatDateTime(time, DateTimeFormat.Date);
    }
  }

  extractDateFromRechartsEventData = (data: any) => {
    if (!data || !data.activePayload || !data.activePayload[0] || !data.activePayload[0].payload) {
      return;
    }
    const dataPoint = data.activePayload[0].payload as RechartsDataPoint;
    return dataPoint.date;
  };

  preventEventPropagation(e?: any) {
    if (!e) {
      return;
    }
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    if (e.preventDefault) {
      e.preventDefault();
    }
  }

  startHighlight = (data?: any, e?: any) => {
    if (!data || !this.props.allowHighlighting) {
      return;
    }
    this.preventEventPropagation(e);

    const date = this.extractDateFromRechartsEventData(data);
    if (!date) {
      return;
    }
    document.addEventListener('keydown', this.onKeyPress, false);
    this.setState({ highlightDateStart: date, highlightDateEnd: undefined });
  };

  updateHighlight = (data: any, e: any) => {
    if (!data || !this.props.allowHighlighting || !this.state.highlightDateStart) {
      return;
    }
    this.preventEventPropagation(e);

    const date = this.extractDateFromRechartsEventData(data);
    if (!date) {
      return;
    }
    this.setState({ highlightDateEnd: date });
  };

  applyHighlight = (data: any, e: any) => {
    const { highlightDateStart, highlightDateEnd } = this.state;
    if (!data || !this.props.allowHighlighting || !highlightDateStart || !highlightDateEnd) {
      return;
    }
    this.preventEventPropagation(e);
    this.endHighlight();
  };

  endHighlight = () => {
    document.removeEventListener('keydown', this.onKeyPress, false);
    this.setState({
      highlightDateStart: undefined,
      highlightDateEnd: undefined,
    });
  };

  onKeyPress = (e: KeyboardEvent) => {
    this.preventEventPropagation(e);
    if (e.keyCode === ESC_KEY_CODE) {
      this.endHighlight();
    }
  };

  renderHighlightedRange = () => {
    const { highlightDateStart, highlightDateEnd } = this.state;
    if (!highlightDateStart || !highlightDateEnd) {
      return;
    }

    const dateRangeMin = this.formatTime(minDateTime([highlightDateStart, highlightDateEnd])!);
    const dateRangeMax = this.formatTime(maxDateTime([highlightDateStart, highlightDateEnd])!);

    // The `label` prop is not documented in the Recharts type specification, but is supported and
    //   included in their public documentation (http://recharts.org/en-US/api/ReferenceLine#label)
    const minReferenceLineProps = {
      label: (p: any): ReactElement<SVGElement> => this.renderHighlightRangeLabel(p, dateRangeMin),
    };
    const maxReferenceLineProps = {
      label: (p: any): ReactElement<SVGElement> => this.renderHighlightRangeLabel(p, dateRangeMax),
    };

    return [
      <ReferenceLine
        key='highlightStartDate'
        x={dateRangeMin}
        stroke={chartReferenceLineColor}
        {...minReferenceLineProps}
      />,
      <ReferenceArea
        key='highlightedArea'
        x1={dateRangeMin}
        x2={dateRangeMax}
        strokeOpacity={0.2}
      />,
      <ReferenceLine
        key='highlightEndDate'
        x={dateRangeMax}
        stroke={chartReferenceLineColor}
        {...maxReferenceLineProps}
      />,
    ];
  };

  renderHighlightRangeLabel(props: ReferenceLineProps, date: string): ReactElement<SVGElement> {
    const { textAnchor, fontSize, viewBox, dy, dx } = props;
    if (!viewBox || !viewBox.x || !viewBox.y) {
      return <svg></svg>;
    }
    return (
      <text
        x={viewBox.x - 20}
        y={viewBox.y - 8}
        dy={dy}
        dx={dx}
        fill='#288DD0'
        fontSize={fontSize || 16}
        fontWeight='bold'
        textAnchor={textAnchor || undefined}
      >
        {date}
      </text>
    );
  }
}
